import React, { useState, useEffect } from 'react';
import { Form, Input, Button, InputNumber, notification, Typography, Table, Card, Row, Col, Space, Statistic, Popconfirm, Tag } from 'antd';
import { DollarOutlined, EditOutlined, DeleteOutlined, UserOutlined, PlusOutlined, CalculatorOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/constants';

const AdvancePaymentForm = () => {
  const [form] = Form.useForm();
  const [totalPay, setTotalPay] = useState(0);
  const [advancePay, setAdvancePay] = useState(0);
  const [remainingPay, setRemainingPay] = useState(0);
  const [narration, setNarration] = useState('');
  const [payments, setPayments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_PAYMENTS);
      if (response.data.success) {
        setPayments(response.data.data);
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'An error occurred while fetching payments',
      });
    }
  };

  const handleSubmit = async (values) => {
    try {
      const url = editingId ? API_ENDPOINTS.UPDATE_PAYMENT : API_ENDPOINTS.CREATE_PAYMENT;
      const method = editingId ? 'PUT' : 'POST';
      const data = {
        id: editingId,
        name: values.name,
        totalPay: values.totalPay,
        advancePay: values.advancePay,
        remainingPay: values.remainingPay,
        narration: values.narration,
      };

      const response = await axios({
        method,
        url,
        headers: {
          'Content-Type': 'application/json',
        },
        data,
      });

      if (response.data.success) {
        notification.success({
          message: 'Success',
          description: `Payment details ${editingId ? 'updated' : 'saved'} successfully!`,
        });

        form.resetFields();
        setTotalPay(0);
        setAdvancePay(0);
        setRemainingPay(0);
        setNarration('');
        setEditingId(null);
        fetchPayments();
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'An unexpected error occurred',
      });
    }
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      name: record.name,
      totalPay: record.total_pay,
      advancePay: record.advance_pay,
      remainingPay: record.remaining_pay,
      narration: record.narration,
    });
    setTotalPay(record.total_pay);
    setAdvancePay(record.advance_pay);
    setRemainingPay(record.remaining_pay);
    setNarration(record.narration);
    setEditingId(record.id);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.post(API_ENDPOINTS.DELETE_PAYMENT, {
        action: 'delete_payment',
        id,
      });
      if (response.data.success) {
        notification.success({
          message: 'Success',
          description: 'Payment details deleted successfully!',
        });
        fetchPayments();
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'An unexpected error occurred',
      });
    }
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const filteredPayments = payments.filter(payment => 
    payment.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: '#',
      width: 50,
      render: (_, __, index) => (
        <span style={{ fontWeight: 500, color: '#666' }}>{index + 1}</span>
      ),
    },
    { 
      title: 'Employee Name', 
      dataIndex: 'name', 
      key: 'name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    { 
      title: 'Total Salary', 
      dataIndex: 'total_pay', 
      key: 'total_pay',
      render: (amount) => <Tag color="blue">₹{parseFloat(amount || 0).toFixed(2)}</Tag>,
    },
    { 
      title: 'Advance Paid', 
      dataIndex: 'advance_pay', 
      key: 'advance_pay',
      render: (amount) => <Tag color="green">₹{parseFloat(amount || 0).toFixed(2)}</Tag>,
    },
    { 
      title: 'Remaining', 
      dataIndex: 'remaining_pay', 
      key: 'remaining_pay',
      render: (amount) => <Tag color="orange">₹{parseFloat(amount || 0).toFixed(2)}</Tag>,
    },
    { 
      title: 'Notes', 
      dataIndex: 'narration', 
      key: 'narration',
      render: (text) => text ? <span style={{ fontSize: '12px', color: '#666' }}>{text}</span> : '-',
    },
    {
      title: 'Actions',
      key: 'action',
      width: 150,
      render: (text, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Payment Record"
            description="Are you sure you want to delete this payment record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button 
              type="primary" 
              danger 
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const totalPayments = payments.reduce((sum, payment) => sum + parseFloat(payment.total_pay || 0), 0);
  const totalAdvances = payments.reduce((sum, payment) => sum + parseFloat(payment.advance_pay || 0), 0);
  const totalRemaining = payments.reduce((sum, payment) => sum + parseFloat(payment.remaining_pay || 0), 0);

  return (
    <div className="fade-in">
      <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  EMPLOYEE RECORDS
                </Typography.Text>
                <Statistic
                  value={payments.length}
                  valueStyle={{ color: '#0F172A', fontWeight: 800, fontSize: '24px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#EEF2FF', color: '#4F46E5' }}>
                <UserOutlined />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  TOTAL PAYROLL BUDGET
                </Typography.Text>
                <Statistic
                  value={totalPayments}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#10B981', fontWeight: 800, fontSize: '24px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#ECFDF5', color: '#10B981' }}>
                <DollarOutlined />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  ADVANCES DISBURSED
                </Typography.Text>
                <Statistic
                  value={totalAdvances}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#F59E0B', fontWeight: 800, fontSize: '24px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#FFFBEB', color: '#F59E0B' }}>
                <DollarOutlined />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  NET SALARY OUTSTANDING
                </Typography.Text>
                <Statistic
                  value={totalRemaining}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#EF4444', fontWeight: 800, fontSize: '24px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#FEF2F2', color: '#EF4444' }}>
                <CalculatorOutlined />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={9}>
          <Card 
            title={
              <Space size="middle">
                <PlusOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>
                  {editingId ? "Edit Payroll Record" : "Add Advance Payment Record"}
                </span>
              </Space>
            }
            style={{ borderRadius: '16px' }}
          >
            <Form form={form} onFinish={handleSubmit} layout="vertical">
              <Form.Item
                name="name"
                label="Employee Name"
                rules={[{ required: true, message: 'Please enter the employee name' }]}
              >
                <Input 
                  placeholder="Full name of staff member" 
                  prefix={<UserOutlined style={{ color: '#94A3B8' }} />}
                  size="large"
                />
              </Form.Item>
              
              <Form.Item
                name="totalPay"
                label="Agreed Total Monthly Salary (₹)"
                rules={[{ required: true, message: 'Please enter total salary' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  value={totalPay}
                  onChange={value => {
                    setTotalPay(value);
                    setRemainingPay((value || 0) - advancePay);
                    form.setFieldsValue({ remainingPay: (value || 0) - advancePay });
                  }}
                  placeholder="0.00"
                  style={{ width: '100%' }}
                  size="large"
                  prefix="₹"
                />
              </Form.Item>
              
              <Form.Item
                name="advancePay"
                label="Advance Payment Disbursed (₹)"
                rules={[{ required: true, message: 'Please enter advance amount' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  value={advancePay}
                  onChange={value => {
                    setAdvancePay(value);
                    setRemainingPay(totalPay - (value || 0));
                    form.setFieldsValue({ remainingPay: totalPay - (value || 0) });
                  }}
                  placeholder="0.00"
                  style={{ width: '100%' }}
                  size="large"
                  prefix="₹"
                />
              </Form.Item>
              
              <Form.Item
                name="remainingPay"
                label="Net Remaining Salary Due (₹)"
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  value={remainingPay}
                  placeholder="Auto-calculated"
                  style={{ width: '100%' }}
                  size="large"
                  prefix="₹"
                  readOnly
                />
              </Form.Item>
              
              <Form.Item
                name="narration"
                label="Remarks / Notes"
              >
                <Input.TextArea
                  value={narration}
                  onChange={e => setNarration(e.target.value)}
                  placeholder="Payment method, date, or reason..."
                  rows={3}
                  style={{ borderRadius: '10px' }}
                />
              </Form.Item>
              
              <Form.Item style={{ marginBottom: 0, marginTop: '20px' }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  {editingId && (
                    <Button 
                      onClick={() => {
                        form.resetFields();
                        setEditingId(null);
                        setTotalPay(0);
                        setAdvancePay(0);
                        setRemainingPay(0);
                        setNarration('');
                      }}
                      size="large"
                      style={{ borderRadius: '10px' }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    icon={editingId ? <EditOutlined /> : <PlusOutlined />}
                    size="large"
                    style={{ borderRadius: '10px' }}
                  >
                    {editingId ? 'Update Record' : 'Log Advance Payment'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        
        <Col xs={24} lg={15}>
          <Card 
            title={
              <Space size="middle">
                <CalculatorOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Payroll & Advances Ledger</span>
              </Space>
            }
            extra={
              <Input.Search
                placeholder="Search staff member..."
                value={searchText}
                onChange={handleSearch}
                style={{ width: 260 }}
                allowClear
                size="large"
              />
            }
            style={{ borderRadius: '16px' }}
          >
            <Table 
              dataSource={filteredPayments} 
              columns={columns} 
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payroll records`
              }}
              scroll={{ x: 750 }}
              size="middle"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};


export default AdvancePaymentForm;
